import React, { useEffect, useState } from "react";
import { logger } from "helpers/logger";
import wrapperHOC from "@Utils/wrapperHOC";
import { LottieAnimatorType } from "./types";
import config from "@Config/index";

/** Frame range of a named marker, or null when the file does not carry one by that name. */
const segmentForMarker = (data: any, marker: string): [number, number] | null => {
  const found = data?.markers?.find((entry: any) => entry?.cm === marker);
  return found ? [found.tm, found.tm + found.dr] : null;
};

const LottieAnimator: React.FC<LottieAnimatorType> = ({
  animationData,
  renderer,
  id,
  width,
  height,
  loop = true,
  onComplete,
  marker,
  onReady,
  onError,
}) => {
  const [animationJsonData, setAnimationData] = useState();
  const S3_BUCKET_URL = (() => {
    if (config.IS_CHINA_APP)
      return `${config.CHINA_STATIC_ASSETS_URL}/image/prod-static-assets/json/`;
    return "https://prod-static-assets.amberstudent.com/json/";
  })();

  const fetchJsonFromS3 = async () => {
    try {
      const response = await fetch(`${S3_BUCKET_URL}${animationData}`);
      const json = await response.json();
      setAnimationData(json);
    } catch (error) {
      logger.error("Error fetching JSON from S3:", error);
      onError?.();
    }
  };

  useEffect(() => {
    fetchJsonFromS3();
  }, []);

  useEffect(() => {
    const container = document.getElementById(id);
    let anim: any = null;
    if (container) {
      import("lottie-web")
        .then((lottie: any) => {
          // Spread rather than passed as `undefined`: with no marker the config object is
          // byte-for-byte what it has always been, so nothing changes for existing callers.
          const segment = marker ? segmentForMarker(animationJsonData, marker) : null;
          anim = lottie.loadAnimation({
            container,
            animationData: animationJsonData,
            renderer,
            loop,
            autoplay: true,
            ...(segment ? { initialSegment: segment } : {}),
          });
          if (onComplete) {
            anim.addEventListener("complete", onComplete);
          }
          onReady?.();
        })
        .catch((error: unknown) => {
          logger.error("Error loading lottie-web:", error);
          onError?.();
        });
    } else {
      // Not a failure: on the first render `animationJsonData` is still loading, so this
      // component returns null and there is no container yet. The effect re-runs when the
      // JSON lands and finds it then — which is why `onError` must NOT fire here.
      logger.log(`Element with ${id} not found`);
    }
    return () => {
      if (anim) anim.destroy();
    };
    // `marker` joins the deps because it selects which frames play; the callbacks stay out,
    // as `onComplete` always has — an inline arrow changes identity every render and would tear
    // the animation down and reload it each time the parent re-renders.
  }, [animationJsonData, id, renderer, loop, marker]);

  return animationJsonData ? (
    <div id={id} style={{ width: width || "100%", height: height || "100%" }} />
  ) : null;
};

export default wrapperHOC(LottieAnimator, {
  componentName: "LottieAnimator",
  showForChina: true,
});
